import type { ConsumableItem } from 'admin/src/types/ConsumableItem';
import type React from 'react';
import { type ReactNode, Suspense, useRef } from 'react';
import { useSuspendedImageData } from 'admin/src/hooks/useSuspendedImageData';
import { NoSymbolIcon } from '@heroicons/react/24/outline';
import { PhotoIcon } from '@heroicons/react/16/solid';
import { useIntersection } from 'react-use';
import { suspensify } from 'suspensify';
import { readFile } from 'admin/src/utils/documents';

type StoreItemImageProps = {
  item: ConsumableItem | undefined;
  className?: string;
};

type BlobImageProps = {
  id?: string;
  className?: string;
  alt?: string;
  imageNotFoundFallback?: ReactNode;
  entityType?: 'employees' | 'equipmenttypes' | 'expenses' | 'votinglocations';
  skipCache?: boolean;
  customerId?: string;
  updatedAt?: string;
};

export function StoreItemImageFallback() {
  return (
    <div className="animate-pulse size-12 flex flex-col items-center justify-center">
      <PhotoIcon className="h-5 w-5 text-gray-800" />
    </div>
  );
}

const useImageFromFileReader = suspensify((attachedReceipt: File) => readFile(attachedReceipt));

export function ImageFromFileReader({ file, classes, alt }: { file: File; classes?: string; alt: string }) {
  const imageString = useImageFromFileReader(file);

  if (!imageString) return null;

  return <img alt={alt ? alt : 'Preview of selected image'} className={classes} src={imageString} />;
}

export function BlobImage({ id, className, alt, imageNotFoundFallback, entityType, customerId, skipCache = false, updatedAt }: BlobImageProps) {
  const image = useSuspendedImageData(id, updatedAt, customerId, skipCache, entityType);

  if (!image.src) {
    return <>{imageNotFoundFallback ? <div>{imageNotFoundFallback}</div> : <NoSymbolIcon className={`scale-75 text-gray-400 ${className}`} />}</>;
  }

  return <img alt={alt} src={image.src} className={className} />;
}

function StoreItemImageInternal({ item, className }: StoreItemImageProps) {
  const itemImage = useSuspendedImageData(`${item?.id}.jpg`, item?.updatedAt, undefined, false, 'equipmenttypes');

  if (!itemImage?.src) {
    return <NoSymbolIcon className={`scale-50 text-gray-400 ${className}`} />;
  }

  return <img alt={`Preview of ${item?.equipTypeName}`} src={itemImage.src} className={className} />;
}

export default function StoreItemImage(props: StoreItemImageProps) {
  const intersectionRef = useRef<HTMLDivElement>(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5,
  });

  let image = null;

  if (intersection?.isIntersecting) {
    image = (
      <Suspense fallback={<StoreItemImageFallback />}>
        <StoreItemImageInternal {...props} />
      </Suspense>
    );
  }

  return (
    <div className={props.className} ref={intersectionRef}>
      {image}
    </div>
  );
}
